<div class="brands-page-container">
  @if (initialLoadFailed() && loadError(); as error) {
    <tf-settings-load-error
      [title]="error"
      [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
      [details]="loadErrorDetails()"
      [retrying]="isLoading()"
      (retry)="reloadBrands()" />
  } @else if (!hasLoadedOnce()) {
    <tf-settings-loading-placeholder [rows]="5" [columns]="5" />
  } @else if (showEmptyState()) {
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="brands-action-error"
      (dismissed)="clearActionError()" />

    <tf-empty-state
      [title]="'SETTINGS_BRANDS.EMPTY.TITLE' | translate"
      [body]="'SETTINGS_BRANDS.EMPTY.BODY' | translate">
      <svg
        empty-state-illustration
        class="settings-empty-illustration brands-empty-illustration"
        width="96"
        height="96"
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true">
        <path class="brands-empty-illustration__awning" d="M22 30H78L84 45H16Z" />
        <path
          class="brands-empty-illustration__stripe"
          d="M31 30L28 45M44 30L42 45M56 30L58 45M69 30L72 45" />
        <rect
          class="brands-empty-illustration__store"
          x="22"
          y="45"
          width="56"
          height="34"
          rx="4" />
        <rect class="brands-empty-illustration__logo" x="34" y="53" width="16" height="16" rx="3" />
        <path class="brands-empty-illustration__line" d="M56 56H70M56 64H66" />
        <path class="brands-empty-illustration__base" d="M18 80H82" />
      </svg>
      <p-button
        empty-state-actions
        icon="pi pi-plus"
        [label]="'SETTINGS_BRANDS.CREATE_BRAND' | translate"
        [disabled]="!canCreateBrand()"
        [pTooltip]="canCreateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createBrand()" />
      <p-button
        empty-state-actions
        icon="pi pi-refresh"
        severity="secondary"
        [loading]="isLoading()"
        [disabled]="isLoading()"
        [ariaLabel]="'SETTINGS_BRANDS.RELOAD_BRANDS' | translate"
        (onClick)="reloadBrands()" />
    </tf-empty-state>
  } @else {
    <tf-settings-list-header
      [searchQuery]="searchQuery()"
      [searchPlaceholder]="'SETTINGS_BRANDS.SEARCH_PLACEHOLDER' | translate"
      [loading]="isLoading()"
      [refreshTooltip]="'SETTINGS_BRANDS.RELOAD_BRANDS' | translate"
      (searchChange)="onSearchChange($event)"
      (refresh)="reloadBrands()">
      <p-button
        headerActions
        [label]="'SETTINGS_BRANDS.CREATE_BRAND' | translate"
        icon="pi pi-plus"
        severity="primary"
        [disabled]="!canCreateBrand()"
        [pTooltip]="canCreateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createBrand()" />
    </tf-settings-list-header>

    <tf-settings-inline-message
      [message]="actionError()"
      messageId="brands-action-error"
      (dismissed)="clearActionError()" />

    <tf-data-table
      [columns]="columns()"
      [value]="editableBrands()"
      [loading]="false"
      [paginator]="true"
      [rows]="50"
      [lazy]="true"
      [totalRecords]="totalRecords()"
      [currentPageReportTemplate]="'SETTINGS_BRANDS.PAGINATION_MESSAGE' | translate"
      styleClass="p-datatable-sm"
      emptyMessageKey="SETTINGS_BRANDS.NO_BRANDS_FOUND"
      (sorted)="onSort($event)"
      (paged)="onPageChange($event)">
      <ng-template tfColumnDef="logoUrl" let-brand>
        <div class="brand-logo-cell">
          @if (brand.logoUrl) {
            <img
              class="brand-logo-thumb"
              [src]="brand.logoUrl"
              [alt]="'SETTINGS_BRANDS.LOGO' | translate"
              loading="lazy" />
          } @else {
            <span class="brand-logo-empty">-</span>
          }
        </div>
      </ng-template>
      <ng-template tfColumnDef="linkedCustomDomains" let-brand>
        <div class="brand-linked-domains-cell">
          @if (visibleLinkedDomains(brand.linkedCustomDomains); as visible) {
            @if (visible.length === 0) {
              <span class="brand-linked-domains-empty">—</span>
            } @else {
              @for (link of visible; track link.domain) {
                <span class="linked-domain-chip linked-domain-chip--compact">
                  <span class="linked-domain-chip-dot" aria-hidden="true"></span>
                  <span class="linked-domain-chip-name">{{ link.domain }}</span>
                </span>
              }
              @if (remainingLinkedDomainsCount(brand.linkedCustomDomains); as extra) {
                <span class="brand-linked-domains-more">+{{ extra }}</span>
              }
            }
          }
        </div>
      </ng-template>
      <ng-template tfColumnDef="linkedTheme" let-brand>
        @if (brand.themeId) {
          @if (linkedThemeForBrand(brand); as theme) {
            <div class="brand-linked-theme-cell" [title]="linkedThemeName(brand)">
              <span class="brand-linked-theme-sr-only">
                {{
                  'SETTINGS_BRANDS.LINKED_THEME_ARIA' | translate: { name: linkedThemeName(brand) }
                }}
              </span>
              <tf-settings-theme-preview
                [theme]="theme.theme"
                mode="light"
                size="mini"
                aria-hidden="true" />
            </div>
          } @else {
            <span
              class="brand-linked-theme-empty"
              [attr.aria-label]="'SETTINGS_BRANDS.NO_LINKED_THEME' | translate">
              —
            </span>
          }
        } @else {
          <span
            class="brand-linked-theme-empty"
            [attr.aria-label]="'SETTINGS_BRANDS.NO_LINKED_THEME' | translate">
            —
          </span>
        }
      </ng-template>
      <ng-template tfColumnDef="_actions" let-brand>
        <div class="actions-cell">
          <p-button
            icon="pi pi-pencil"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canUpdateBrand()"
            [pTooltip]="
              canUpdateBrand()
                ? ('GENERAL.EDIT' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            (onClick)="editBrand(brand)" />
          <p-button
            icon="pi pi-trash"
            severity="danger"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canDeleteBrand()"
            [pTooltip]="
              canDeleteBrand()
                ? ('GENERAL.DELETE' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            (onClick)="deleteBrand(brand)" />
        </div>
      </ng-template>
    </tf-data-table>
  }

  <p-dialog
    [visible]="isEditDialogVisible()"
    (visibleChange)="onEditDialogVisibleChange($event)"
    [modal]="true"
    [style]="{ width: '40rem' }"
    header="{{ 'SETTINGS_BRANDS.EDIT_BRAND' | translate }}"
    [draggable]="false"
    [resizable]="false">
    <form [formGroup]="editBrandForm" class="brand-form">
      <tf-settings-inline-message
        [message]="dialogError()"
        messageId="edit-brand-dialog-error"
        (dismissed)="clearDialogError()" />

      <div class="form-field">
        <label for="editBrandName">
          {{ 'SETTINGS_BRANDS.NAME' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="editBrandName"
          type="text"
          formControlName="name"
          [placeholder]="'SETTINGS_BRANDS.NAME_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['name']"
          [attr.aria-invalid]="editServerErrors.value()['name'] ? true : null"
          [attr.aria-describedby]="
            editServerErrors.value()['name'] ? 'editBrandName-error' : null
          " />
        @if (
          editBrandForm.get('name')?.hasError('required') && editBrandForm.get('name')?.touched
        ) {
          <small class="p-error">{{ 'SETTINGS_BRANDS.NAME_REQUIRED' | translate }}</small>
        }
        @if (editServerErrors.value()['name']; as error) {
          <small class="p-error" id="editBrandName-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="editBrandEmail">{{ 'SETTINGS_BRANDS.EMAIL' | translate }}</label>
        <input
          pInputText
          id="editBrandEmail"
          type="text"
          formControlName="email"
          [placeholder]="'SETTINGS_BRANDS.EMAIL_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['email']"
          [attr.aria-invalid]="editServerErrors.value()['email'] ? true : null"
          [attr.aria-describedby]="
            editServerErrors.value()['email'] ? 'editBrandEmail-error' : null
          " />
        @if (editBrandForm.get('email')?.hasError('email') && editBrandForm.get('email')?.touched) {
          <small class="p-error">{{ 'SETTINGS_BRANDS.EMAIL_INVALID' | translate }}</small>
        }
        @if (editServerErrors.value()['email']; as error) {
          <small class="p-error" id="editBrandEmail-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="editBrandPhone">{{ 'SETTINGS_BRANDS.PHONE_NUMBER' | translate }}</label>
        <input
          pInputText
          id="editBrandPhone"
          type="text"
          formControlName="phoneNumber"
          [placeholder]="'SETTINGS_BRANDS.PHONE_NUMBER_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['phoneNumber']"
          [attr.aria-invalid]="editServerErrors.value()['phoneNumber'] ? true : null"
          [attr.aria-describedby]="
            editServerErrors.value()['phoneNumber'] ? 'editBrandPhone-error' : null
          " />
        @if (editServerErrors.value()['phoneNumber']; as error) {
          <small class="p-error" id="editBrandPhone-error" role="alert">{{ error }}</small>
        }
      </div>

      @if (editLinkedDomainsSectionVisible()) {
        <div class="form-field linked-domains-section">
          <div class="linked-domains-header">
            <span class="form-field__label">
              {{ 'SETTINGS_BRANDS.LINKED_CUSTOM_DOMAINS' | translate }}
            </span>
            <a
              class="linked-domains-manage"
              routerLink="/settings/custom-domains"
              (click)="closeEditDialog()">
              {{ 'SETTINGS_BRANDS.MANAGE_CUSTOM_DOMAINS' | translate }}
              <i class="pi pi-arrow-right"></i>
            </a>
          </div>
          <small>{{ 'SETTINGS_BRANDS.LINKED_CUSTOM_DOMAINS_HELP' | translate }}</small>

          <div class="linked-domain-chips">
            @for (link of editLinkedDomains(); track link.id) {
              <span class="linked-domain-chip">
                <span class="linked-domain-chip-dot" aria-hidden="true"></span>
                <span class="linked-domain-chip-name">{{ link.domain }}</span>
                <p-button
                  icon="pi pi-times"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  [disabled]="!canUpdateBrand() || isMutatingLink() === link.id"
                  [pTooltip]="
                    canUpdateBrand()
                      ? ('SETTINGS_BRANDS.UNLINK_DOMAIN' | translate)
                      : ('GENERAL.NO_PERMISSION' | translate)
                  "
                  tooltipPosition="top"
                  [attr.aria-label]="'SETTINGS_BRANDS.UNLINK_DOMAIN' | translate"
                  (onClick)="unlinkDomain(link)" />
              </span>
            }
            @if (availableCustomDomains().length > 0) {
              @if (isPickingDomain()) {
                <p-select
                  appendTo="body"
                  [options]="availableCustomDomains()"
                  optionLabel="domain"
                  styleClass="linked-domain-picker"
                  [filter]="true"
                  filterBy="domain"
                  [placeholder]="'SETTINGS_BRANDS.PICK_DOMAIN_PLACEHOLDER' | translate"
                  (onChange)="linkDomain($event.value)"
                  (onHide)="isPickingDomain.set(false)" />
              } @else {
                <p-button
                  icon="pi pi-plus"
                  severity="secondary"
                  size="small"
                  [outlined]="true"
                  [label]="'SETTINGS_BRANDS.ADD_LINKED_DOMAIN' | translate"
                  [disabled]="!canUpdateBrand()"
                  [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="toggleDomainPicker()" />
              }
            }
          </div>
        </div>
      }

      @if (canReadThemes()) {
        <div class="form-field theme-link-section">
          <div class="linked-domains-header">
            <span class="form-field__label">
              {{ 'SETTINGS_BRANDS.THEME' | translate }}
            </span>
            <a
              class="linked-domains-manage"
              routerLink="/settings/theme"
              (click)="closeEditDialog()">
              {{ 'SETTINGS_BRANDS.MANAGE_THEMES' | translate }}
              <i class="pi pi-arrow-right"></i>
            </a>
          </div>
          <small>{{ 'SETTINGS_BRANDS.THEME_HELP' | translate }}</small>

          <div class="theme-choice-list">
            <label class="theme-choice" [class.selected]="!editBrandForm.controls.themeId.value">
              <input
                type="radio"
                name="editBrandThemeMode"
                [checked]="!editBrandForm.controls.themeId.value"
                (change)="useWorkspaceTheme('edit')" />
              <span class="theme-choice__radio" aria-hidden="true"></span>
              <span class="theme-choice__body">
                <span class="theme-choice__title">
                  {{ 'SETTINGS_BRANDS.USE_WORKSPACE_THEME' | translate }}
                </span>
                <span class="theme-choice__description">
                  {{
                    'SETTINGS_BRANDS.USE_WORKSPACE_THEME_DESC'
                      | translate: { name: activeWorkspaceTheme()?.name ?? '-' }
                  }}
                </span>
              </span>
              @if (activeWorkspaceTheme(); as workspaceTheme) {
                <span class="theme-choice__preview-pair">
                  <tf-settings-theme-preview
                    [theme]="workspaceTheme.theme"
                    mode="light"
                    size="mini" />
                  <tf-settings-theme-preview
                    [theme]="workspaceTheme.theme"
                    mode="dark"
                    size="mini" />
                </span>
              }
            </label>

            <label
              class="theme-choice"
              [class.selected]="!!editBrandForm.controls.themeId.value"
              [class.disabled]="customThemes().length === 0">
              <input
                type="radio"
                name="editBrandThemeMode"
                [checked]="!!editBrandForm.controls.themeId.value"
                [disabled]="customThemes().length === 0"
                (change)="useCustomTheme('edit')" />
              <span class="theme-choice__radio" aria-hidden="true"></span>
              <span class="theme-choice__body">
                <span class="theme-choice__title">
                  {{ 'SETTINGS_BRANDS.LINK_CUSTOM_THEME' | translate }}
                </span>
                <span class="theme-choice__description">
                  {{ 'SETTINGS_BRANDS.LINK_CUSTOM_THEME_DESC' | translate }}
                </span>
                @if (customThemes().length > 0) {
                  <p-select
                    appendTo="body"
                    formControlName="themeId"
                    [options]="customThemes()"
                    optionLabel="name"
                    optionValue="id"
                    styleClass="brand-theme-picker"
                    [placeholder]="'SETTINGS_BRANDS.PICK_THEME_PLACEHOLDER' | translate"
                    (onShow)="useCustomTheme('edit')">
                    <ng-template #item let-theme>
                      <div class="brand-theme-option">
                        <span class="brand-theme-option__previews">
                          <tf-settings-theme-preview
                            [theme]="theme.theme"
                            mode="light"
                            size="mini" />
                          <tf-settings-theme-preview
                            [theme]="theme.theme"
                            mode="dark"
                            size="mini" />
                        </span>
                        <span class="brand-theme-option__text">
                          <span>{{ theme.name }}</span>
                          @if (theme.description) {
                            <small>{{ theme.description }}</small>
                          }
                        </span>
                      </div>
                    </ng-template>
                  </p-select>
                } @else {
                  <span class="theme-choice__empty">
                    {{ 'SETTINGS_BRANDS.NO_CUSTOM_THEMES' | translate }}
                  </span>
                }
              </span>
              @if (selectedEditTheme(); as selectedTheme) {
                <span class="theme-choice__preview-pair">
                  <tf-settings-theme-preview
                    [theme]="selectedTheme.theme"
                    mode="light"
                    size="mini" />
                  <tf-settings-theme-preview
                    [theme]="selectedTheme.theme"
                    mode="dark"
                    size="mini" />
                </span>
              }
            </label>
          </div>
        </div>
      }

      <div class="branding-list">
        <div class="branding-card">
          <div class="branding-card__preview">
            @if (editLogoCardPreviewUrl()) {
              <img
                [src]="editLogoCardPreviewUrl()!"
                [alt]="'SETTINGS_BRANDS.LOGO' | translate"
                class="branding-image branding-image--logo" />
            } @else {
              <div class="branding-placeholder" aria-hidden="true">
                <i class="pi pi-image"></i>
              </div>
            }
          </div>
          <div class="branding-card__content">
            <div class="branding-card__title">
              {{ 'SETTINGS_BRANDS.LOGO_CARD_TITLE' | translate }}
            </div>
            <div class="branding-card__description">
              {{
                (originalBrand()?.logoUrl
                  ? 'SETTINGS_BRANDS.LOGO_CONFIGURED'
                  : 'SETTINGS_BRANDS.LOGO_DEFAULT_IN_USE'
                ) | translate
              }}
            </div>
            <small>{{ 'SETTINGS_BRANDS.LOGO_HELP' | translate }}</small>
            @if (removeExistingLogo()) {
              <small class="branding-status">
                {{ 'SETTINGS_BRANDS.LOGO_WILL_BE_REMOVED' | translate }}
              </small>
            }
            @if (isUploadingLogo() && logoUploadMessage()) {
              <small class="branding-status">{{ logoUploadMessage() }}</small>
            }
            @if (logoError(); as error) {
              <small class="p-error" id="edit-brand-logo-error" role="alert">{{ error }}</small>
            }
          </div>
          <div class="branding-card__actions">
            <input
              #editBrandLogoInput
              id="editBrandLogoFile"
              type="file"
              accept="image/png,image/jpeg"
              (change)="onEditLogoSelected($event)"
              class="branding-upload-input" />
            <p-button
              [label]="
                originalBrand()?.logoUrl
                  ? ('SETTINGS_BRANDS.REPLACE_LOGO' | translate)
                  : ('SETTINGS_BRANDS.UPLOAD_LOGO' | translate)
              "
              icon="pi pi-upload"
              severity="secondary"
              [outlined]="true"
              [disabled]="isLoading() || !canUpdateBrand()"
              [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
              tooltipPosition="top"
              (onClick)="editBrandLogoInput.click()" />
            @if (removeExistingLogo()) {
              <p-button
                [label]="'SETTINGS_BRANDS.KEEP_LOGO' | translate"
                icon="pi pi-undo"
                severity="secondary"
                [outlined]="true"
                [disabled]="isLoading() || !canUpdateBrand()"
                [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="undoLogoRemoval()" />
            } @else if (originalBrand()?.logoUrl && !editLogoFile()) {
              <p-button
                [label]="'SETTINGS_BRANDS.REMOVE_LOGO' | translate"
                icon="pi pi-trash"
                severity="danger"
                [outlined]="true"
                [disabled]="isLoading() || !canUpdateBrand()"
                [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="markLogoForRemoval()" />
            }
          </div>
          @if (editLogoFile()) {
            <div class="branding-selection-row">
              <span>{{ editLogoFile()?.name }}</span>
              <p-button
                icon="pi pi-times"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                [disabled]="isLoading()"
                [pTooltip]="'GENERAL.CLEAR' | translate"
                tooltipPosition="top"
                (onClick)="clearEditLogoSelection(editBrandLogoInput)" />
            </div>
          }
        </div>

        <div class="branding-card">
          <div class="branding-card__preview">
            @if (editFaviconPreviewUrl()) {
              <img
                [src]="editFaviconPreviewUrl()!"
                [alt]="'SETTINGS_BRANDS.FAVICON' | translate"
                class="branding-image branding-image--favicon" />
            } @else {
              <div class="branding-placeholder" aria-hidden="true">
                <i class="pi pi-image"></i>
              </div>
            }
          </div>
          <div class="branding-card__content">
            <div class="branding-card__title">
              {{ 'SETTINGS_BRANDS.FAVICON_CARD_TITLE' | translate }}
            </div>
            <div class="branding-card__description">
              {{
                (originalBrand()?.faviconUrl
                  ? 'SETTINGS_BRANDS.FAVICON_CONFIGURED'
                  : 'SETTINGS_BRANDS.FAVICON_DEFAULT_IN_USE'
                ) | translate
              }}
            </div>
            <small>{{ 'SETTINGS_BRANDS.FAVICON_HELP' | translate }}</small>
            @if (removeExistingFavicon()) {
              <small class="branding-status">
                {{ 'SETTINGS_BRANDS.FAVICON_WILL_BE_REMOVED' | translate }}
              </small>
            }
            @if (isUploadingFavicon() && faviconUploadMessage()) {
              <small class="branding-status">{{ faviconUploadMessage() }}</small>
            }
            @if (faviconError(); as error) {
              <small class="p-error" id="edit-brand-favicon-error" role="alert">{{ error }}</small>
            }
          </div>
          <div class="branding-card__actions">
            <input
              #editBrandFaviconInput
              id="editBrandFaviconFile"
              type="file"
              accept="image/png"
              (change)="onEditFaviconSelected($event)"
              class="branding-upload-input" />
            <p-button
              [label]="
                originalBrand()?.faviconUrl
                  ? ('SETTINGS_BRANDS.REPLACE_FAVICON' | translate)
                  : ('SETTINGS_BRANDS.UPLOAD_FAVICON' | translate)
              "
              icon="pi pi-upload"
              severity="secondary"
              [outlined]="true"
              [disabled]="isLoading() || !canUpdateBrand()"
              [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
              tooltipPosition="top"
              (onClick)="editBrandFaviconInput.click()" />
            @if (removeExistingFavicon()) {
              <p-button
                [label]="'SETTINGS_BRANDS.KEEP_FAVICON' | translate"
                icon="pi pi-undo"
                severity="secondary"
                [outlined]="true"
                [disabled]="isLoading() || !canUpdateBrand()"
                [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="undoFaviconRemoval()" />
            } @else if (originalBrand()?.faviconUrl && !editFaviconFile()) {
              <p-button
                [label]="'SETTINGS_BRANDS.REMOVE_FAVICON' | translate"
                icon="pi pi-trash"
                severity="danger"
                [outlined]="true"
                [disabled]="isLoading() || !canUpdateBrand()"
                [pTooltip]="canUpdateBrand() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="markFaviconForRemoval()" />
            }
          </div>
          @if (editFaviconFile()) {
            <div class="branding-selection-row">
              <span>{{ editFaviconFile()?.name }}</span>
              <p-button
                icon="pi pi-times"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                [disabled]="isLoading()"
                [pTooltip]="'GENERAL.CLEAR' | translate"
                tooltipPosition="top"
                (onClick)="clearEditFaviconSelection(editBrandFaviconInput)" />
            </div>
          }
        </div>
      </div>
    </form>

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        [disabled]="isLoading()"
        (onClick)="closeEditDialog()" />
      <p-button
        label="{{ 'GENERAL.SAVE' | translate }}"
        severity="primary"
        [loading]="isLoading()"
        [disabled]="
          editBrandForm.invalid ||
          isLoading() ||
          (editBrandForm.pristine &&
            !editLogoFile() &&
            !removeExistingLogo() &&
            !editFaviconFile() &&
            !removeExistingFavicon())
        "
        (onClick)="saveEditing()" />
    </ng-template>
  </p-dialog>

  <p-dialog
    [visible]="isCreateDialogVisible()"
    (visibleChange)="onCreateDialogVisibleChange($event)"
    [modal]="true"
    [style]="{ width: '40rem' }"
    header="{{ 'SETTINGS_BRANDS.CREATE_BRAND' | translate }}"
    [draggable]="false"
    [resizable]="false">
    <form [formGroup]="createBrandForm" class="brand-form">
      <tf-settings-inline-message
        [message]="dialogError()"
        messageId="create-brand-dialog-error"
        (dismissed)="clearDialogError()" />

      <div class="form-field">
        <label for="newBrandName">
          {{ 'SETTINGS_BRANDS.NAME' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="newBrandName"
          type="text"
          formControlName="name"
          [placeholder]="'SETTINGS_BRANDS.NAME_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['name']"
          [attr.aria-invalid]="createServerErrors.value()['name'] ? true : null"
          [attr.aria-describedby]="
            createServerErrors.value()['name'] ? 'newBrandName-error' : null
          " />
        @if (
          createBrandForm.get('name')?.hasError('required') && createBrandForm.get('name')?.touched
        ) {
          <small class="p-error">{{ 'SETTINGS_BRANDS.NAME_REQUIRED' | translate }}</small>
        }
        @if (createServerErrors.value()['name']; as error) {
          <small class="p-error" id="newBrandName-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="newBrandEmail">{{ 'SETTINGS_BRANDS.EMAIL' | translate }}</label>
        <input
          pInputText
          id="newBrandEmail"
          type="text"
          formControlName="email"
          [placeholder]="'SETTINGS_BRANDS.EMAIL_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['email']"
          [attr.aria-invalid]="createServerErrors.value()['email'] ? true : null"
          [attr.aria-describedby]="
            createServerErrors.value()['email'] ? 'newBrandEmail-error' : null
          " />
        @if (
          createBrandForm.get('email')?.hasError('email') && createBrandForm.get('email')?.touched
        ) {
          <small class="p-error">{{ 'SETTINGS_BRANDS.EMAIL_INVALID' | translate }}</small>
        }
        @if (createServerErrors.value()['email']; as error) {
          <small class="p-error" id="newBrandEmail-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="newBrandPhone">{{ 'SETTINGS_BRANDS.PHONE_NUMBER' | translate }}</label>
        <input
          pInputText
          id="newBrandPhone"
          type="text"
          formControlName="phoneNumber"
          [placeholder]="'SETTINGS_BRANDS.PHONE_NUMBER_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['phoneNumber']"
          [attr.aria-invalid]="createServerErrors.value()['phoneNumber'] ? true : null"
          [attr.aria-describedby]="
            createServerErrors.value()['phoneNumber'] ? 'newBrandPhone-error' : null
          " />
        @if (createServerErrors.value()['phoneNumber']; as error) {
          <small class="p-error" id="newBrandPhone-error" role="alert">{{ error }}</small>
        }
      </div>

      @if (createLinkedDomainsSectionVisible()) {
        <div class="form-field linked-domains-section">
          <div class="linked-domains-header">
            <span class="form-field__label">
              {{ 'SETTINGS_BRANDS.LINKED_CUSTOM_DOMAINS' | translate }}
            </span>
          </div>
          <small>{{ 'SETTINGS_BRANDS.LINKED_CUSTOM_DOMAINS_HELP' | translate }}</small>

          <div class="linked-domain-chips">
            @for (domain of createStagedDomains(); track domain.id) {
              <span class="linked-domain-chip">
                <span class="linked-domain-chip-dot" aria-hidden="true"></span>
                <span class="linked-domain-chip-name">{{ domain.domain }}</span>
                <p-button
                  icon="pi pi-times"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  [pTooltip]="'SETTINGS_BRANDS.UNLINK_DOMAIN' | translate"
                  tooltipPosition="top"
                  [attr.aria-label]="'SETTINGS_BRANDS.UNLINK_DOMAIN' | translate"
                  (onClick)="unstageDomainForCreate(domain.id)" />
              </span>
            }
            @if (availableCustomDomainsForCreate().length > 0) {
              @if (isPickingDomainForCreate()) {
                <p-select
                  appendTo="body"
                  [options]="availableCustomDomainsForCreate()"
                  optionLabel="domain"
                  styleClass="linked-domain-picker"
                  [filter]="true"
                  filterBy="domain"
                  [placeholder]="'SETTINGS_BRANDS.PICK_DOMAIN_PLACEHOLDER' | translate"
                  (onChange)="stageDomainForCreate($event.value)"
                  (onHide)="isPickingDomainForCreate.set(false)" />
              } @else {
                <p-button
                  icon="pi pi-plus"
                  severity="secondary"
                  size="small"
                  [outlined]="true"
                  [label]="'SETTINGS_BRANDS.ADD_LINKED_DOMAIN' | translate"
                  (onClick)="toggleDomainPickerForCreate()" />
              }
            }
          </div>
        </div>
      }

      @if (canReadThemes()) {
        <div class="form-field theme-link-section">
          <div class="linked-domains-header">
            <span class="form-field__label">
              {{ 'SETTINGS_BRANDS.THEME' | translate }}
            </span>
            <a
              class="linked-domains-manage"
              routerLink="/settings/theme"
              (click)="closeCreateDialog()">
              {{ 'SETTINGS_BRANDS.MANAGE_THEMES' | translate }}
              <i class="pi pi-arrow-right"></i>
            </a>
          </div>
          <small>{{ 'SETTINGS_BRANDS.THEME_HELP' | translate }}</small>

          <div class="theme-choice-list">
            <label class="theme-choice" [class.selected]="!createBrandForm.controls.themeId.value">
              <input
                type="radio"
                name="createBrandThemeMode"
                [checked]="!createBrandForm.controls.themeId.value"
                (change)="useWorkspaceTheme('create')" />
              <span class="theme-choice__radio" aria-hidden="true"></span>
              <span class="theme-choice__body">
                <span class="theme-choice__title">
                  {{ 'SETTINGS_BRANDS.USE_WORKSPACE_THEME' | translate }}
                </span>
                <span class="theme-choice__description">
                  {{
                    'SETTINGS_BRANDS.USE_WORKSPACE_THEME_DESC'
                      | translate: { name: activeWorkspaceTheme()?.name ?? '-' }
                  }}
                </span>
              </span>
              @if (activeWorkspaceTheme(); as workspaceTheme) {
                <span class="theme-choice__preview-pair">
                  <tf-settings-theme-preview
                    [theme]="workspaceTheme.theme"
                    mode="light"
                    size="mini" />
                  <tf-settings-theme-preview
                    [theme]="workspaceTheme.theme"
                    mode="dark"
                    size="mini" />
                </span>
              }
            </label>

            <label
              class="theme-choice"
              [class.selected]="!!createBrandForm.controls.themeId.value"
              [class.disabled]="customThemes().length === 0">
              <input
                type="radio"
                name="createBrandThemeMode"
                [checked]="!!createBrandForm.controls.themeId.value"
                [disabled]="customThemes().length === 0"
                (change)="useCustomTheme('create')" />
              <span class="theme-choice__radio" aria-hidden="true"></span>
              <span class="theme-choice__body">
                <span class="theme-choice__title">
                  {{ 'SETTINGS_BRANDS.LINK_CUSTOM_THEME' | translate }}
                </span>
                <span class="theme-choice__description">
                  {{ 'SETTINGS_BRANDS.LINK_CUSTOM_THEME_DESC' | translate }}
                </span>
                @if (customThemes().length > 0) {
                  <p-select
                    appendTo="body"
                    formControlName="themeId"
                    [options]="customThemes()"
                    optionLabel="name"
                    optionValue="id"
                    styleClass="brand-theme-picker"
                    [placeholder]="'SETTINGS_BRANDS.PICK_THEME_PLACEHOLDER' | translate"
                    (onShow)="useCustomTheme('create')">
                    <ng-template #item let-theme>
                      <div class="brand-theme-option">
                        <span class="brand-theme-option__previews">
                          <tf-settings-theme-preview
                            [theme]="theme.theme"
                            mode="light"
                            size="mini" />
                          <tf-settings-theme-preview
                            [theme]="theme.theme"
                            mode="dark"
                            size="mini" />
                        </span>
                        <span class="brand-theme-option__text">
                          <span>{{ theme.name }}</span>
                          @if (theme.description) {
                            <small>{{ theme.description }}</small>
                          }
                        </span>
                      </div>
                    </ng-template>
                  </p-select>
                } @else {
                  <span class="theme-choice__empty">
                    {{ 'SETTINGS_BRANDS.NO_CUSTOM_THEMES' | translate }}
                  </span>
                }
              </span>
              @if (selectedCreateTheme(); as selectedTheme) {
                <span class="theme-choice__preview-pair">
                  <tf-settings-theme-preview
                    [theme]="selectedTheme.theme"
                    mode="light"
                    size="mini" />
                  <tf-settings-theme-preview
                    [theme]="selectedTheme.theme"
                    mode="dark"
                    size="mini" />
                </span>
              }
            </label>
          </div>
        </div>
      }

      <div class="branding-list">
        <div class="branding-card">
          <div class="branding-card__preview">
            @if (createLogoPreviewUrl()) {
              <img
                [src]="createLogoPreviewUrl()!"
                [alt]="'SETTINGS_BRANDS.LOGO' | translate"
                class="branding-image branding-image--logo" />
            } @else {
              <div class="branding-placeholder" aria-hidden="true">
                <i class="pi pi-image"></i>
              </div>
            }
          </div>
          <div class="branding-card__content">
            <div class="branding-card__title">
              {{ 'SETTINGS_BRANDS.LOGO_CARD_TITLE' | translate }}
            </div>
            <div class="branding-card__description">
              {{ 'SETTINGS_BRANDS.LOGO_DEFAULT_IN_USE' | translate }}
            </div>
            <small>{{ 'SETTINGS_BRANDS.LOGO_HELP' | translate }}</small>
            @if (isUploadingLogo() && logoUploadMessage()) {
              <small class="branding-status">{{ logoUploadMessage() }}</small>
            }
            @if (logoError(); as error) {
              <small class="p-error" id="create-brand-logo-error" role="alert">{{ error }}</small>
            }
          </div>
          <div class="branding-card__actions">
            <input
              #createBrandLogoInput
              id="createBrandLogoFile"
              type="file"
              accept="image/png,image/jpeg"
              (change)="onCreateLogoSelected($event)"
              class="branding-upload-input" />
            <p-button
              [label]="'SETTINGS_BRANDS.UPLOAD_LOGO' | translate"
              icon="pi pi-upload"
              severity="secondary"
              [outlined]="true"
              [disabled]="isLoading()"
              (onClick)="createBrandLogoInput.click()" />
          </div>
          @if (createLogoFile()) {
            <div class="branding-selection-row">
              <span>{{ createLogoFile()?.name }}</span>
              <p-button
                icon="pi pi-times"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                [disabled]="isLoading()"
                [pTooltip]="'GENERAL.CLEAR' | translate"
                tooltipPosition="top"
                (onClick)="clearCreateLogoSelection(createBrandLogoInput)" />
            </div>
          }
        </div>

        <div class="branding-card">
          <div class="branding-card__preview">
            @if (createFaviconPreviewUrl()) {
              <img
                [src]="createFaviconPreviewUrl()!"
                [alt]="'SETTINGS_BRANDS.FAVICON' | translate"
                class="branding-image branding-image--favicon" />
            } @else {
              <div class="branding-placeholder" aria-hidden="true">
                <i class="pi pi-image"></i>
              </div>
            }
          </div>
          <div class="branding-card__content">
            <div class="branding-card__title">
              {{ 'SETTINGS_BRANDS.FAVICON_CARD_TITLE' | translate }}
            </div>
            <div class="branding-card__description">
              {{ 'SETTINGS_BRANDS.FAVICON_DEFAULT_IN_USE' | translate }}
            </div>
            <small>{{ 'SETTINGS_BRANDS.FAVICON_HELP' | translate }}</small>
            @if (isUploadingFavicon() && faviconUploadMessage()) {
              <small class="branding-status">{{ faviconUploadMessage() }}</small>
            }
            @if (faviconError(); as error) {
              <small class="p-error" id="create-brand-favicon-error" role="alert">{{ error }}</small>
            }
          </div>
          <div class="branding-card__actions">
            <input
              #createBrandFaviconInput
              id="createBrandFaviconFile"
              type="file"
              accept="image/png"
              (change)="onCreateFaviconSelected($event)"
              class="branding-upload-input" />
            <p-button
              [label]="'SETTINGS_BRANDS.UPLOAD_FAVICON' | translate"
              icon="pi pi-upload"
              severity="secondary"
              [outlined]="true"
              [disabled]="isLoading()"
              (onClick)="createBrandFaviconInput.click()" />
          </div>
          @if (createFaviconFile()) {
            <div class="branding-selection-row">
              <span>{{ createFaviconFile()?.name }}</span>
              <p-button
                icon="pi pi-times"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                [disabled]="isLoading()"
                [pTooltip]="'GENERAL.CLEAR' | translate"
                tooltipPosition="top"
                (onClick)="clearCreateFaviconSelection(createBrandFaviconInput)" />
            </div>
          }
        </div>
      </div>
    </form>

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        [disabled]="isLoading()"
        (onClick)="closeCreateDialog()" />
      <p-button
        label="{{ 'GENERAL.SAVE' | translate }}"
        severity="primary"
        [loading]="isLoading()"
        [disabled]="createBrandForm.invalid || isLoading()"
        (onClick)="saveNewBrand()" />
    </ng-template>
  </p-dialog>

  <p-confirmDialog />
</div>
